import { useParams } from 'react-router-dom'

import dayjs from 'dayjs'

import { usePersonaStatsApi } from '@/common/api/modules/persona'

import Heading from '@/components/view/atoms/heading/Heading'
import PersonaAvatar from '@/components/view/atoms/image/PersonaAvatar'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import ContentEditLoading from '@/components/view/molecules/loading/ContentEditLoading'
import PageEditLoading from '@/components/view/molecules/loading/PageEditLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import MeasurementConfigEditor from '@/components/container/modules/persona/MeasurementConfigEditor'

const Metric = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <div className='min-w-[140px]'>
    <Text size={12} color={TextColor.SECONDARY}>
      {label}
    </Text>
    <div>
      <Text size={24} weight='bold'>
        {value}
      </Text>
    </div>
    {note && (
      <div>
        <Text size={10} color={TextColor.TERTIARY}>
          {note}
        </Text>
      </div>
    )}
  </div>
)

const PersonaAssignmentPage = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)
  const { data, isLoading } = usePersonaStatsApi({ site_id: siteId })

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='判定・割当'
        description='該当は多対多（1人が複数のペルソナに該当してよい）。割当は排他で、効果測定の単位になります。'
      />
      <Content>
        {isLoading || !data ? (
          <ContentEditLoading center />
        ) : (
          <>
            <Area>
              <Heading
                subText={
                  data.last_judged_at
                    ? `最終判定 ${dayjs(data.last_judged_at).format('YYYY/MM/DD HH:mm')}`
                    : 'まだ判定が実行されていません'
                }
              >
                件数
              </Heading>
              <AreaContent>
                <div className='flex flex-wrap gap-x-10 gap-y-4'>
                  <Metric label='顧客' value={`${data.customers.toLocaleString()} 人`} />
                  <Metric
                    label='1体以上に該当'
                    value={`${data.matched_customers.toLocaleString()} 人`}
                    note={
                      data.customers
                        ? `${Math.round((data.matched_customers / data.customers) * 100)}%`
                        : undefined
                    }
                  />
                  <Metric
                    label='該当ののべ件数'
                    value={`${data.matches.toLocaleString()} 件`}
                    note={
                      data.matched_customers
                        ? `1人あたり ${(data.matches / data.matched_customers).toFixed(2)} 体`
                        : undefined
                    }
                  />
                  <Metric
                    label='現行の割当'
                    value={`${data.assigned.toLocaleString()} 人`}
                    note={`履歴 ${data.assignment_history.toLocaleString()} 行`}
                  />
                  <Metric
                    label='採用 / 有効'
                    value={`${data.adopted} / ${data.active}`}
                    note={data.undecidable ? `判定不能 ${data.undecidable} 体` : undefined}
                  />
                </div>
              </AreaContent>
            </Area>

            <Area>
              <Heading subText='同じ軸の競合はスコアで一意化し、そのうえで ①条件の具体性 ②合致度スコア ③サイトの優先度 ④uplift実績 の順に比較します。'>
                主ペルソナの決め手
              </Heading>
              <AreaContent>
                {data.decided_by.length === 0 ? (
                  <Text size={13} color={TextColor.SECONDARY}>
                    有効化したペルソナがまだありません。ライブラリで有効化すると割当が始まります。
                  </Text>
                ) : (
                  <div className='flex flex-wrap gap-x-10 gap-y-3'>
                    {data.decided_by.map((d) => (
                      <Metric
                        key={d.rule}
                        label={d.label}
                        value={`${d.customers.toLocaleString()} 人`}
                      />
                    ))}
                  </div>
                )}
              </AreaContent>
            </Area>

            <MeasurementConfigEditor siteId={siteId} />

            <Area>
              <Heading>ペルソナごとの該当</Heading>
              <AreaContent>
                <Table>
                  <thead>
                    <TableRow head>
                      <TableHeadItem>
                        <Text size={12}>ペルソナ</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>状態</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>該当</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>判定条件（原文）</Text>
                      </TableHeadItem>
                    </TableRow>
                  </thead>
                  <tbody>
                    {data.by_persona.map((p) => (
                      <TableRow key={p.persona_id}>
                        <TableRowItem>
                          <div className='flex items-center gap-2'>
                            <PersonaAvatar
                              emoji={p.avatar_emoji}
                              color={p.avatar_color}
                              muted={p.matched_size === null}
                            />
                            <div className='min-w-0'>
                              <Text size={14}>{p.name}</Text>
                              <div>
                                <Text size={10} color={TextColor.TERTIARY}>
                                  {p.persona_id}
                                </Text>
                              </div>
                            </div>
                          </div>
                        </TableRowItem>
                        <TableRowItem>
                          {p.active ? (
                            <Text size={12} color={TextColor.SUCCESS} nowrap>
                              有効
                            </Text>
                          ) : (
                            <Text size={12} color={TextColor.SECONDARY} nowrap>
                              採用のみ
                            </Text>
                          )}
                          {p.measuring_until && (
                            <div>
                              <Text size={10} color={TextColor.TERTIARY}>
                                測定中 〜{p.measuring_until}
                              </Text>
                            </div>
                          )}
                        </TableRowItem>
                        <TableRowItem>
                          {p.matched_size === null ? (
                            <Text size={12} color={TextColor.SECONDARY} nowrap>
                              判定不能
                            </Text>
                          ) : (
                            <Text size={14} weight='bold' nowrap>
                              {p.matched_size.toLocaleString()} 人
                            </Text>
                          )}
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12} color={TextColor.SECONDARY}>
                            {p.judge_text}
                          </Text>
                        </TableRowItem>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </AreaContent>
            </Area>
          </>
        )}
      </Content>
    </Main>
  )
}

export default PersonaAssignmentPage
